import { defaultMemoize } from 'reselect';

import getConfig from '../env';

// Layouts selectors and transforms
export const formatPoints = defaultMemoize((points = '0') => parseInt(points, 10).toLocaleString().replace('.00', ''));

export const getFriendlyDate = defaultMemoize((awardExpiresDate, currentDate = new Date()) => {
  if (awardExpiresDate.getFullYear() - currentDate.getFullYear() > 10 ||
      // eslint-disable-next-line no-restricted-globals
      isNaN(awardExpiresDate.getFullYear())) {
    return 'N/A';
  }
  return awardExpiresDate.toDateString();
});

export const generateBankPoints = defaultMemoize((
  {
    id = '', isAccessCodeBank = false, isPointsBank = false, points = 0
  },
  awards = []
) => {
  if (isPointsBank && points) {
    return points;
  } else if (isAccessCodeBank) {
    return awards.filter((award) => (award.bank ? id === award.bank.id : true)).length.toString();
  }
  return '0';
});

export const selectCompiledCatalogPoints = defaultMemoize(({
  banks = [
    {
      isPointsBank: false,
      isCurrentBank: false,
      points: 0
    }
  ],
  awards = []
} = {}) => {
  let pointsCount = 0;
  pointsCount = awards.length > 0 ? awards.length : pointsCount;
  let bankFilter = banks.filter((bank) => bank.isPointsBank && bank.points > 0);
  pointsCount = bankFilter.length > 0
    ? bankFilter.reduce((acc, bank) => `${acc} ${formatPoints(bank.points)}`, '')
    : formatPoints(pointsCount);
  bankFilter = banks.filter((bank) => bank.isCurrentBank && bank.isPointsBank && bank.points > 0);
  pointsCount = bankFilter.length > 0
    ? bankFilter.reduce((acc, bank) => `${acc} ${formatPoints(bank.points)}`, '')
    : pointsCount;
  return pointsCount;
});

export const selectCompiledCatalogLink = defaultMemoize(({
  banks = [
    {
      isPointsBank: false,
      isCurrentBank: false,
      points: 0,
      link: ''
    }
  ],
  redeem = {
    link: ''
  },
  catalogs = []
} = {}) => {
  let catalogLink = '#';
  let bankFilter = banks.filter(
    (bank) => bank.points > 0 && bank.link && bank.link.indexOf('viewAwards') === -1
  );
  catalogLink = bankFilter.length > 0 ? bankFilter[0].link : catalogLink;
  catalogLink = catalogs.length > 0 ? catalogs[0].link : catalogLink;
  catalogLink = redeem.link ? redeem.link : catalogLink;
  bankFilter = banks.filter((bank) => bank.isPointsBank && bank.points > 0);
  catalogLink = bankFilter.length > 0 ? bankFilter.slice(-1)[0].link : catalogLink;
  bankFilter = banks.filter((bank) => bank.isCurrentBank && bank.isPointsBank && bank.points > 0);
  catalogLink = bankFilter.length > 0 ? bankFilter.slice(-1)[0].link : catalogLink;
  if (banks && banks.length > 0 && banks[0].isAccessCodeBank) {
    catalogLink = banks[0].link;
  }
  return (catalogLink.replace(/cart/i, ''));
});

export const selectTransformedBanks = defaultMemoize(({
  banks = [],
  awards = []
} = {}) => banks.map((bank) =>
  Object.assign({}, bank, {
    link: bank.link,
    points: formatPoints(generateBankPoints(bank, awards))
  })));

export const selectTransformedAwards = defaultMemoize(({
  awards = []
} = {}) => awards.map((award) =>
  Object.assign({}, award, {
    link: award.link,
    expires: getFriendlyDate(new Date(award.expires))
  })));

export const selectTransformedCatalogs = defaultMemoize(({
  catalogs = []
} = {}) => catalogs.map((catalog) =>
  Object.assign({}, catalog, {
    link: catalog.link
  })));

export const selectLogoutLinks = defaultMemoize(({ logout = [] } = {}) => logout);

// alerts selectors

export const getRecipientId = defaultMemoize((link = '') => {
  const matches = /printEProduct\.seam\?recipientId=([0-9]+)/g.exec(link);
  return matches ? matches[1] : '';
});

export const getReceiveLink = defaultMemoize((title = '', recipientId, alertId) => {
  if (title.toLowerCase() === 'view' && recipientId) {
    return `${getConfig('LEGACY_ROOT_URL')}/receiveECard.seam?eId=${recipientId}&alertId=${alertId}`;
  }
  return '/ui/greatwork/history.html';
});

export const flatten = defaultMemoize((arr) =>
  arr.reduce((acc, val) => acc.concat(Array.isArray(val) ? flatten(val) : val), []));

export const getAlertsId = defaultMemoize((link = '') => {
  const matches = /\/api\/alerts\/([0-9]+)/g.exec(link);
  return matches ? matches[1] : '';
});

export const selectRedeemLink = defaultMemoize((receiveCollectionItem) => {
  const filteredList = receiveCollectionItem.links.filter(
    (link) => link.link.indexOf('catalog.jsf') > -1
  );
  if (filteredList.length > 0) return filteredList.slice(-1)[0].link;
  return '';
});

export const selectRedeemTitle = defaultMemoize((receiveCollectionItem) => {
  const filteredList = receiveCollectionItem.links.filter(
    (link) => link.link.indexOf('catalog.jsf') > -1
  );
  if (filteredList.length > 0) return filteredList.slice(-1)[0].title;
  return '';
});

export const selectReceiveLink = defaultMemoize(({ links = [], href = '' } = {}) => {
  let receiveCollectionItemActions = links.filter(
    (link) => link.actions && link.actions.length > 0
  );
  receiveCollectionItemActions = receiveCollectionItemActions.length > 0
    ? receiveCollectionItemActions.map((link) =>
      link.actions.filter((action) => action.link.indexOf('printEProduct.seam') > -1))
    : [];

  receiveCollectionItemActions = flatten(receiveCollectionItemActions);

  const recipientId = receiveCollectionItemActions.length > 0
    ? getRecipientId(receiveCollectionItemActions.slice(-1)[0].link)
    : 0;

  const hasViewLink =
      links.filter((link) => !!link.title && link.title.toLowerCase() === 'view')
        .length > 0;

  return getReceiveLink(
    hasViewLink ? 'view' : '',
    recipientId,
    getAlertsId(href)
  );
});

export const normalizeNavLink = defaultMemoize((link = '#') => {
  if (link.indexOf('https://appreciatehub.com') === 0) {
    return link.replace('https://appreciatehub.com', 'https://www.appreciatehub.com');
  }
  return link;
});

const buildGenericAlertArray = (...Arguments) =>
  Arguments.filter((cur) => !!cur).reduce((acc, cur) => {
    // This is to fix an inconsistant layout of alerts that are within
    // perfork, its a hack, yes, but its an easy one.
    if (Array.isArray(cur)) {
      // eslint-disable-next-line no-param-reassign
      // eslint-disable-next-line prefer-destructuring
      // eslint-disable-next-line
      cur = cur[0];
    }
    return acc.concat({
      title: cur.title,
      href: normalizeNavLink(cur.href)
    });
  }, []);

export const selectCompiledAlertsCount = defaultMemoize((
  {
    pendingApprovals = {
      count: 0
    },
    pendingPresentations = {
      count: 0
    },
    additionalInformation = {
      count: 0
    },
    passwordExpired = {
      count: 0
    },
    newReports = {
      count: 0
    },
    scheduledReports = {
      count: 0
    },
    proxyAssignment = {
      count: 0
    },
    pointsExpiring = {
      count: 0
    },
    accessCodeExpiring = {
      count: 0
    },
    receive = {
      count: 0
    }
  } = {}
) =>
  pendingApprovals.count +
    pendingPresentations.count +
    additionalInformation.count +
    passwordExpired.count +
    newReports.count +
    scheduledReports.count +
    proxyAssignment.count +
    pointsExpiring.count +
    accessCodeExpiring.count +
    receive.count);

export const selectCompiledGenericAlertsArray = defaultMemoize((
  {
    pendingApprovals = {
      link: ''
    },
    pendingPresentations = {
      link: ''
    },
    additionalInformation = {
      link: ''
    },
    passwordExpired = {
      link: ''
    },
    newReports = {
      collection: []
    },
    scheduledReports = {
      collection: []
    },
    proxyAssignment = {
      collection: []
    },
    pointsExpiring = {
      collection: []
    },
    accessCodeExpiring = {
      collection: []
    }
  } = {}
) =>
  [].concat(
    buildGenericAlertArray(
      pendingApprovals.link,
      pendingPresentations.link,
      additionalInformation.link,
      passwordExpired.link,
      ...newReports.collection,
      ...scheduledReports.collection,
      ...proxyAssignment.collection,
      ...pointsExpiring.collection,
      ...accessCodeExpiring.collection
    )
  ));

export const selectCompiledReceiveAlertsArray = defaultMemoize((
  {
    receive = {
      collection: []
    }
  } = {}
) =>
  [].concat(
    receive.collection.map((receiveItem) => ({
      href: selectReceiveLink(receiveItem),
      title: receiveItem.title,
      redeem: {
        href: selectRedeemLink(receiveItem),
        title: selectRedeemTitle(receiveItem)
      }
    }))
  ));
